/**
 * GlowCursor - Soft glow effect that follows the cursor
 *
 * Creates an ambient light effect around the mouse position.
 */

"use client";

import { useEffect, useState, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";

interface Position {
  x: number;
  y: number;
}

const GLOW_SIZE = 350;
const GLOW_OPACITY = 0.1;

export default function GlowCursor() {
  const shouldReduceMotion = useReducedMotion();
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [isVisible, setIsVisible] = useState(false);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    setPosition({ x: e.clientX, y: e.clientY });
    setIsVisible(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsVisible(false);
  }, []);

  useEffect(() => {
    if (shouldReduceMotion) return;

    window.addEventListener("mousemove", handleMouseMove);
    document.body.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      document.body.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [shouldReduceMotion, handleMouseMove, handleMouseLeave]);

  if (shouldReduceMotion) {
    return null;
  }

  return (
    <motion.div
      className="fixed pointer-events-none z-0 rounded-full bg-gradient-radial from-pink-500 to-purple-500 blur-[100px]"
      style={{
        width: GLOW_SIZE,
        height: GLOW_SIZE,
        left: position.x - GLOW_SIZE / 2,
        top: position.y - GLOW_SIZE / 2,
      }}
      animate={{
        opacity: isVisible ? GLOW_OPACITY : 0,
        scale: isVisible ? 1 : 0.8,
      }}
      transition={{
        type: "spring",
        stiffness: 150,
        damping: 15,
        opacity: { duration: 0.2 },
      }}
    />
  );
}
